import React, { useState, useCallback, useEffect, useRef } from 'react';
import AppIcon from '../common/AppIcon';
import GradientIcon from '../common/GradientIcon';
import { motion } from 'framer-motion';
import { apiService, getImgUrl } from '../../services/api';

// 🟢 引入通用组件 & Hook
import Modal from '../common/Modal';
import ConfirmDialog from '../common/ConfirmDialog';
import Pagination from '../common/Pagination';
import { useServerPagination } from '../../hooks/useServerPagination';

const AdminPage = () => {
  // ==========================================
  // 1. Hooks & State (状态管理)
  // ==========================================
  const [activeTab, setActiveTab] = useState('users');

  // ==========================================
  // 图片上传相关状态管理
  // ==========================================
  const [selectedImageFile, setSelectedImageFile] = useState(null); // 本地文件对象
  const [localPreviewUrl, setLocalPreviewUrl] = useState(null);     // 本地 Blob 预览
  const [isUploadingImage, setIsUploadingImage] = useState(false);  //上传 Loading
  const [fileInputKey, setFileInputKey] = useState(Date.now());     // 🔑 关键：用于强制重置 input DOM
  const [duplicateWarning, setDuplicateWarning] = useState(''); // 🟢 [新增] 查重警告状态

  // API 包装函数
  const fetchAllUsersAPI = useCallback((params) => apiService.getAllUsers(params), []); 
  const fetchVocabularyAPI = useCallback((params) => apiService.getVocabulary(params), []);
  // 🟢 [新增] 获取学校列表 API (假设后端有此接口，或使用通用 client)
  const fetchSchoolsAPI = useCallback(async (params) => {
    try {
      const res = await apiService.client.get('/schools', { params });
      
      // 1. 获取纯数组数据 (兼容拦截器)
      let list = [];
      if (Array.isArray(res)) list = res;
      else if (res.data && Array.isArray(res.data)) list = res.data;
      else if (res.schools) list = res.schools;

      // 2. 简单的前端过滤 (因为后端目前返回所有数据)
      if (params.search) {
        const lowerSearch = params.search.toLowerCase();
        list = list.filter(s => 
          s.name.toLowerCase().includes(lowerSearch) || 
          (s.region && s.region.toLowerCase().includes(lowerSearch))
        );
      }

      // 3. 返回 Hook 期望的对象结构
      // useServerPagination 通常期望 { data: Array, total: Number }
      // 或者 { items: Array, totalItems: Number }，这里我们尽量都覆盖
      return {
        data: list,           // 数据列表
        items: list,          // 兼容性字段
        total: list.length,   // 总数
        totalItems: list.length // 兼容性字段
      };
    } catch (error) {
      console.error("Fetch schools failed:", error);
      return { data: [], total: 0 }; // 出错时返回空结构，防止 Hook 崩溃
    }
  }, []);

  // --- 分页 Hooks ---
  const pendingUsersPager = useServerPagination(fetchAllUsersAPI, { status: 'pending' }, 50, 'users');
  const approvedUsersPager = useServerPagination(fetchAllUsersAPI, { status: 'approved' }, 10, 'users');
  const vocabPager = useServerPagination(fetchVocabularyAPI, { status: 'all' }, 10, 'vocabulary');
  // 🟢 [新增] 学校分页
  const schoolsPager = useServerPagination(fetchSchoolsAPI, { search: '' }, 10, 'data');

  // --- UI 状态 ---
  const [isWordModalOpen, setIsWordModalOpen] = useState(false);
  const [isSchoolModalOpen, setIsSchoolModalOpen] = useState(false); // 🟢 [新增]
  // 🟢 [Req3] 班级管理（在学校下创建/查看班级）
  const [isClassMgmtOpen, setIsClassMgmtOpen] = useState(false);
  const [classMgmtSchool, setClassMgmtSchool] = useState(null);
  const [classMgmtList, setClassMgmtList] = useState([]);
  const [classMgmtLoading, setClassMgmtLoading] = useState(false);
  const [newClassName, setNewClassName] = useState('');
  const [creatingClass, setCreatingClass] = useState(false);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  
  // 词汇统计
  const [vocabStats, setVocabStats] = useState({ total: 0, approved: 0, pending: 0, rejected: 0 });

  // 确认弹窗
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false, type: null, id: null, title: '', message: '', data: null, isDestructive: false
  });
  const [isActionLoading, setIsActionLoading] = useState(false);

  // 表单数据 - 词汇
  const [editingId, setEditingId] = useState(null);
  const [newWord, setNewWord] = useState({
    word: '', definition: '', level: 'core', partOfSpeech: 'noun', 
    synonyms: ['', '', ''], example: '', imageUrl: '' 
  });

  // 🟢 [新增] 表单数据 - 学校
  const [editingSchoolId, setEditingSchoolId] = useState(null);
  const [newSchool, setNewSchool] = useState({
    name: '', region: 'UK', type: 'Primary', contactEmail: ''
  });

  // 🟢 [新增] 表单数据 - 用户编辑
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingUserForm, setEditingUserForm] = useState({ role: 'student', schoolId: '', classIds: [] });
  const [allSchools, setAllSchools] = useState([]);
  const [schoolClasses, setSchoolClasses] = useState([]);

  // 设置 & Dashboard
  const [settings, setSettings] = useState({ autoApprove: false, autoApproveStudents: false, emailNotifs: true });
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);

  // 搜索状态
  const [searchFilters, setSearchFilters] = useState({ search: '', level: 'all', status: 'all' });
  const [schoolSearch, setSchoolSearch] = useState(''); // 🟢 [新增]

  // ==========================================
  // 2. Effects
  // ==========================================

  // Vocabulary 防抖搜索
  useEffect(() => {
    const timer = setTimeout(() => {
      // 1. 获取当前输入并去空格
      const rawSearch = searchFilters.search ? searchFilters.search.trim() : '';
      
      // 2. 🔍 [核心规则] 字数检查
      // 如果用户正在输入 (长度 > 0)，但还没输完 3 个字 (长度 < 3)
      // -> 直接 return，什么都不做，也不发请求
      if (rawSearch.length > 0 && rawSearch.length < 3) {
        return; 
      }

      // 3. 🧹 [数据清洗] 准备发送给后端的参数
      // 只有满足 >= 3 个字，才把搜索词发给后端；否则发 null
      const safeSearch = rawSearch.length >= 3 ? rawSearch : null;
      
      // 确保 'all' 被转换为 null，配合后端逻辑
      const safeLevel = searchFilters.level === 'all' ? null : searchFilters.level;
      const safeStatus = searchFilters.status === 'all' ? null : searchFilters.status;

      // 4. 🚀 [触发] 发送请求
      // 这一步会调用 useServerPagination 里的 refresh，并自动处理 loading 状态
      vocabPager.updateFilters({
        search: safeSearch,
        level: safeLevel,
        status: safeStatus
      });

    }, 500); // 500ms 防抖，给用户思考和打字的时间

    return () => clearTimeout(timer);
  }, [searchFilters.search, searchFilters.level, searchFilters.status]);

  // 🟢 [新增] Schools 防抖搜索
  useEffect(() => {
    const timer = setTimeout(() => {
      schoolsPager.updateFilters({ search: schoolSearch });
    }, 500);
    return () => clearTimeout(timer);
  }, [schoolSearch]);

  // Tab 切换逻辑
  useEffect(() => {
    if (activeTab === 'vocabulary') fetchVocabStats();
    if (activeTab === 'analytics') loadDashboard();
    if (activeTab === 'settings') loadSettings();
  }, [activeTab]);

  const loadDashboard = async () => {
    setDashboardLoading(true);
    try {
      const res = await apiService.getDashboardAnalytics();
      setDashboardData(res);
    } catch (e) { console.error(e); } finally { setDashboardLoading(false); }
  };

  const loadSettings = async () => {
    setLoadingSettings(true);
    try {
      const data = await apiService.getSettings();
      setSettings(data);
    } catch (error) { console.error(error); } finally { setLoadingSettings(false); }
  };

  const handleToggle = async (key) => {
    const oldSettings = { ...settings };
    const newSettings = { ...settings, [key]: !settings[key] };
    setSettings(newSettings);
    try { await apiService.updateSettings({ [key]: newSettings[key] }); } 
    catch (error) { console.error(error); setSettings(oldSettings); alert("Failed to save."); }
  };

  // ==========================================
  // 3. 辅助函数 & Action Handlers
  // ==========================================
  
  const formatDate = (dateObj) => {
    if (!dateObj) return 'N/A';
    const date = dateObj._seconds ? new Date(dateObj._seconds * 1000) : new Date(dateObj);
    return date.toLocaleDateString('en-CA');
  };

  const openConfirm = (type, id, title, message, isDestructive = false, data = null) => {
    setConfirmDialog({ isOpen: true, type, id, title, message, isDestructive, data });
  };
  const closeConfirm = () => { if (!isActionLoading) setConfirmDialog(prev => ({ ...prev, isOpen: false })); };

   const fetchVocabStats = async () => {
    try {
      const [allRes, approvedRes, pendingRes, rejectedRes] = await Promise.all([
        apiService.getVocabulary({ status: 'all', limit: 1 }),
        apiService.getVocabulary({ status: 'approved', limit: 1 }),
        apiService.getVocabulary({ status: 'pending', limit: 1 }),
        apiService.getVocabulary({ status: 'rejected', limit: 1 })
      ]);
      setVocabStats({
        total: allRes.total || 0,
        approved: approvedRes.total || 0,
        pending: pendingRes.total || 0,
        rejected: rejectedRes.total || 0
      });
    } catch (error) { console.error(error); }
  };

  // --- Handlers: Vocabulary ---
  const handleSynonymChange = (index, value) => { /* ... existing logic ... */ 
    const updated = [...newWord.synonyms]; updated[index] = value; setNewWord(prev => ({ ...prev, synonyms: updated })); 
  };
  const handleEditClick = (word) => { /* ... existing logic ... */ 
    setNewWord({ word: word.word, definition: word.definition, level: word.level, partOfSpeech: word.partOfSpeech||'noun', synonyms: word.synonyms||['','',''], example: word.example, imageUrl: word.imageUrl||'' });
    setEditingId(word.id); setIsWordModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsWordModalOpen(false); setEditingId(null);
    setNewWord({ word: '', definition: '', level: 'core', partOfSpeech: 'noun', synonyms: ['', '', ''], example: '', imageUrl: '' });
    setDuplicateWarning(''); // 🟢 [新增] 重置警告
  };
  const handleAddWordSubmit = async (e) => {
    e.preventDefault();
    if (isSubmittingForm) return; // 防止重复点击
    setIsSubmittingForm(true);

    try {
      // 1. 构建基础数据 (先不包含 status)
      const basePayload = {
        ...newWord,
        word: newWord.word.toLowerCase(),
        synonyms: newWord.synonyms.filter(s => s.trim() !== ''),
        imageUrl: newWord.imageUrl || ''
      };

      if (editingId) {
        // 🟢 [修复] 编辑模式：绝对不发送 status 字段！
        // 这样后端只会更新 word/definition 等内容，原有的 status (pending/rejected) 会被保留
        await apiService.updateVocabulary(editingId, basePayload);
      } else {
        // 🟢 创建模式：如果是管理员手动添加，默认为 pending
        const createPayload = { ...basePayload, status: 'pending' };
        await apiService.createVocabulary(createPayload);
      }

      vocabPager.refresh();
      fetchVocabStats();
      handleCloseModal();
    } catch (e) {
      alert(e.message);
    } finally {
      setIsSubmittingForm(false);
    }
  };

  // --- 🟢 [新增] Handlers: Schools ---
  const handleEditSchoolClick = (school) => {
    setNewSchool({ name: school.name, region: school.region || 'UK', type: school.type || 'Primary', contactEmail: school.contactEmail || '' });
    setEditingSchoolId(school.id);
    setIsSchoolModalOpen(true);
  };

  const handleCloseSchoolModal = () => {
    setIsSchoolModalOpen(false); setEditingSchoolId(null);
    setNewSchool({ name: '', region: 'UK', type: 'Primary', contactEmail: '' });
  };

  const handleAddSchoolSubmit = async (e) => {
    e.preventDefault();
    if (isSubmittingForm) return;
    setIsSubmittingForm(true);
    try {
      if (editingSchoolId) {
        await apiService.client.put(`/schools/${editingSchoolId}`, newSchool);
      } else {
        await apiService.client.post('/schools', newSchool);
      }
      schoolsPager.refresh();
      handleCloseSchoolModal();
    } catch (error) {
      console.error(error); alert(`Failed to ${editingSchoolId ? 'update' : 'add'} school: ` + error.message);
    } finally { setIsSubmittingForm(false); }
  };

  // --- 🟢 [新增] Handlers: User Edit ---
  const handleEditUserClick = async (user) => {
    setEditingUserId(user.id);
    setEditingUserForm({
      role: user.role || 'student',
      schoolId: user.schoolId || '',
      classIds: user.classIds || []
    });
    
    if (allSchools.length === 0) {
      try {
        const res = await apiService.client.get('/schools');
        setAllSchools(res.data || res.schools || res || []);
      } catch(e) {}
    }
    
    if (user.schoolId) {
      fetchClassesForSchool(user.schoolId);
    } else {
      setSchoolClasses([]);
    }
    
    setIsUserModalOpen(true);
  };

  const fetchClassesForSchool = async (schoolId) => {
    try {
      const cRes = await apiService.client.get('/classes');
      let list = cRes.data || cRes.classes || cRes || [];
      // We filter classes by schoolId if available. If a class has no schoolId in DB, it might be legacy, so we optionally include it or skip it.
      setSchoolClasses(list.filter(c => c.schoolId === schoolId || !c.schoolId));
    } catch(e) {}
  };

  // 🟢 [Req3] 班级管理 handlers（管理员在学校下建/看班级）
  const fetchClassMgmt = async (schoolId) => {
    setClassMgmtLoading(true);
    try {
      const cRes = await apiService.client.get('/classes');
      const list = cRes.data || cRes.classes || cRes || [];
      setClassMgmtList(list.filter(c => c.schoolId === schoolId));
    } catch (e) { console.error('Load classes failed', e); }
    finally { setClassMgmtLoading(false); }
  };

  const openClassMgmt = (school) => {
    setClassMgmtSchool(school);
    setNewClassName('');
    setClassMgmtList([]);
    setIsClassMgmtOpen(true);
    fetchClassMgmt(school.id);
  };

  const closeClassMgmt = () => {
    setIsClassMgmtOpen(false);
    setClassMgmtSchool(null);
    setNewClassName('');
  };

  const handleCreateClass = async (e) => {
    e.preventDefault();
    if (creatingClass || !newClassName.trim() || !classMgmtSchool) return;
    setCreatingClass(true);
    try {
      await apiService.createClass({ name: newClassName.trim(), schoolId: classMgmtSchool.id });
      setNewClassName('');
      await fetchClassMgmt(classMgmtSchool.id);
    } catch (err) {
      console.error(err); alert('Failed to create class: ' + err.message);
    } finally { setCreatingClass(false); }
  };

  const handleSchoolChange = (e) => {
    const sid = e.target.value;
    setEditingUserForm(prev => ({ ...prev, schoolId: sid, classIds: [] }));
    if (sid) fetchClassesForSchool(sid);
    else setSchoolClasses([]);
  };

  const handleCloseUserModal = () => {
    setIsUserModalOpen(false);
    setEditingUserId(null);
    setEditingUserForm({ role: 'student', schoolId: '', classIds: [] });
  };

  const handleEditUserSubmit = async (e) => {
    e.preventDefault();
    if (isSubmittingForm) return;
    setIsSubmittingForm(true);
    try {
      await apiService.updateUser(editingUserId, editingUserForm);
      approvedUsersPager.refresh();
      handleCloseUserModal();
    } catch (error) {
      console.error(error); alert('Failed to update user: ' + error.message);
    } finally { setIsSubmittingForm(false); }
  };

  // --- Generic Action Confirm ---
  const handleConfirmAction = async () => {
    if (isActionLoading) return;
    setIsActionLoading(true);
    try {
      const { type, id, data } = confirmDialog;
      if (type === 'approve_user') { await apiService.approveUser(id, 'approved'); pendingUsersPager.refresh(); approvedUsersPager.refresh(); }
      else if (type === 'reject_user' || type === 'delete_user') { await apiService.deleteUser(id); if (type === 'reject_user') pendingUsersPager.refresh(); else approvedUsersPager.refresh(); }
      else if (type === 'delete_word') { await apiService.client.delete(`/vocabulary/${id}`); vocabPager.refresh(); fetchVocabStats(); }
      else if (type === 'update_word_status') { await apiService.updateVocabulary(id, { status: data }); vocabPager.refresh(); fetchVocabStats(); }
      // 🟢 [新增] Delete School
      else if (type === 'delete_school') { await apiService.client.delete(`/schools/${id}`); schoolsPager.refresh(); }
      // 🟢 [新增] 处理生成课程的逻辑
      else if (type === 'generate_curriculum') {
          const res = await apiService.generateCurriculum();
          // 生成成功后，关闭弹窗 (ConfirmDialog 会自动关闭)
          // 如果您有 Toast 组件，可以在这里弹出 "Success" 提示
          console.log("Curriculum generated:", res); 
      }
      // 🟢 [新增] 结束
      closeConfirm();
    } catch (error) { console.error(error); alert(`Error: ${error.message}`); } 
    finally { setIsActionLoading(false); }
  };

  // ==========================================
  // lessons 相关
  // ==========================================

  //单词查重
  // 🟢 [新增] 失焦查重函数
  const handleWordBlur = async () => {
    const targetWord = newWord.word ? newWord.word.trim().toLowerCase() : '';

    // 如果为空，不查，且确保清空之前的警告
    if (!targetWord) {
      setDuplicateWarning('');
      return;
    }

    try {
      // 调用搜索接口 (查前5个匹配项)
      const res = await apiService.getVocabulary({ search: targetWord, limit: 5 });
      
      // 精确匹配检查 (必须排除掉“自己”，防止编辑时报错说和自己重复)
      const exactMatch = res.vocabulary.find(w => 
        w.word.toLowerCase() === targetWord && w.id !== editingId
      );

      if (exactMatch) {
        setDuplicateWarning(`⚠️ Word "${exactMatch.word}" already exists!`);
      }
    } catch (error) {
      console.error("Duplicate check failed:", error);
    }
  };

  const handleGenerateLessons = () => {
    openConfirm(
        'generate_curriculum', // 1. 定义一个新的操作类型
        null,                  // 2. 这里不需要 ID
        'Generate Curriculum', // 3. 弹窗标题
        '⚠️ This will RESTRUCTURE the curriculum based on the current vocabulary list.\n\n• Existing lessons with missing words will be repaired.\n• Unassigned words will be grouped into new lessons (12 words/lesson).', // 4. 弹窗内容
        false // 5. 是否为破坏性操作（红色按钮）？设为 false 显示蓝色，设为 true 显示红色
    );
  };
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);

const clearImageState = () => {
    // 1. 释放本地预览 URL 的内存 (防止内存泄漏)
    if (localPreviewUrl) {
      URL.revokeObjectURL(localPreviewUrl);
    }
    // 2. 重置所有图片相关状态
    setSelectedImageFile(null);
    setLocalPreviewUrl(null);
    setIsUploadingImage(false);
    // 3. 强制重置文件输入框 DOM
    setFileInputKey(Date.now());
  };

  // 监听 Modal 关闭，执行深度清理
  useEffect(() => {
    if (!isWordModalOpen) {
      clearImageState();
      // 这里的 setNewWord 是为了确保表单数据也被重置
      // 如果你在 handleCloseModal 里已经重置了 newWord，这里主要起双重保险作用
    }
  }, [isWordModalOpen]);
  // ==========================================
  // 🟢 [图片上传] 严格的交互处理
  // ==========================================

  // 1. 处理文件选择
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // 校验文件类型
    if (!file.type.startsWith('image/')) {
      alert('Strict Check: Only image files are allowed.');
      return;
    }

    // 清理之前的本地预览（如果有）
    if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);

    setSelectedImageFile(file);
    setLocalPreviewUrl(URL.createObjectURL(file));

    // ⚠️ 严格逻辑：如果用户重新选择了文件，我们将暂时视为“未上传”状态
    // 即使 newWord.imageUrl 里有值（之前的图），我们也优先显示本地预览等待上传
  };

  // 2. 单词输入变更时的严格检查
  const handleWordChange = (e) => {
    const newText = e.target.value;
    
    // 1. 用户只要打字，就视为“正在修正”，立即清空查重警告
    if (duplicateWarning) {
        setDuplicateWarning('');
    }

    // 2. ⚠️ 严格逻辑：防止文件名与单词不匹配
    // 如果用户已经上传了图片 (newWord.imageUrl 有值)，但又修改了单词
    // 我们必须清空已上传的图片路径，迫使基于新单词重新上传
    if (newWord.imageUrl && newWord.imageUrl.includes('vocabulary/')) {
       // 只有当图片是系统自动命名的（包含 vocabulary 路径）才执行此操作，防止误删手动填写的外部 URL
       // 但根据你的需求，现在都是自动上传，所以可以大胆清空
       setNewWord(prev => ({ ...prev, word: newText, imageUrl: '' }));
       // 可选：给个轻微提示
       // console.log("Word changed, image url reset to ensure naming consistency.");
    } else {
       setNewWord(prev => ({ ...prev, word: newText }));
    }
  };

  // 3. 执行上传
  const handleUploadClick = async () => {
    if (!selectedImageFile) return;
    
    const currentWord = newWord.word ? newWord.word.trim() : '';

    // 校验：单词必须存在且长度合格
    if (!currentWord || currentWord.length < 2) {
      alert("Strict Check: Please enter a valid 'Word' (at least 2 chars) before uploading.");
      return;
    }

    setIsUploadingImage(true);

    try {
      const formData = new FormData();
      formData.append('image', selectedImageFile);
      formData.append('word', currentWord); // 传给后端用于命名

      const res = await apiService.client.post('/vocabulary/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (res.imageUrl || res.data?.imageUrl) {
        const uploadedPath = res.imageUrl || res.data.imageUrl;
        
        // 更新表单数据
        setNewWord(prev => ({ ...prev, imageUrl: uploadedPath }));
        
        // ✅ 上传成功后，清理本地文件状态，切换为显示远程图片
        // 这样用户就能明确知道“现在显示的是服务器上的图”
        if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
        setSelectedImageFile(null);
        setLocalPreviewUrl(null); 
        
        // 强制 Input 组件刷新，防止显示文件名
        setFileInputKey(Date.now());
      }
    } catch (error) {
      console.error("Upload failed:", error);
      alert("Upload Error: " + (error.response?.data?.error || error.message));
    } finally {
      setIsUploadingImage(false);
    }
  };


  // ==========================================
  // 4. Renderers (子组件渲染)
  // ==========================================

  const renderUserManagement = () => { /* ... keeping exact existing logic ... */ 
    const { data: pendingUsers, loading: loadingPending } = pendingUsersPager;
    const { data: approvedUsers, loading: loadingApproved, currentPage, totalPages, goToPage, totalItems } = approvedUsersPager;
    return (
      <div className="space-y-6">
        <div className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-6"><AppIcon name="clock" className="mr-2" /> Pending Approvals ({pendingUsers.length})</h2>
          {loadingPending ? <div className="text-gray-500">Loading...</div> : pendingUsers.length > 0 ? (
            <div className="space-y-4">
              {pendingUsers.map((user) => (
                <div key={user.id} className="bg-white bg-opacity-20 rounded-lg p-4 flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-bold text-gray-800">{user.name || 'Unknown'}</h3>
                    <p className="text-sm opacity-90 text-gray-700">{user.email}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`text-xs px-2 py-1 rounded-full font-bold text-white ${user.role === 'teacher' ? 'bg-blue-500' : 'bg-green-500'}`}>{user.role}</span>
                      {/* 直接显示 schoolName */}
                      {user.schoolName && (
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded border border-gray-200">
                          <AppIcon name="school" className="mr-1" /> {user.schoolName}
                        </span>
                      )}
                      
                      {/* 直接显示 classNames 数组 */}
                      {user.classNames && user.classNames.length > 0 && (
                        <div className="flex gap-1">
                          {user.classNames.map((clsName, idx) => (
                            <span key={idx} className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded border border-blue-100">
                              <AppIcon name="users" className="mr-1" /> {clsName}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button onClick={() => openConfirm('approve_user', user.id, 'Approve User', `Approve ${user.name}?`)} className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-semibold shadow-sm">Approve</button>
                    <button onClick={() => openConfirm('reject_user', user.id, 'Reject User', `Reject ${user.name}?`, true)} className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-semibold shadow-sm">Reject</button>
                  </div>
                </div>
              ))}
            </div>
          ) : <div className="text-center text-gray-600 opacity-75 p-4">No pending approvals</div>}
        </div>
        <div className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-6"><AppIcon name="check" className="mr-2" /> Approved Users ({totalItems})</h2>
          {loadingApproved ? <div className="text-center p-8">Loading users...</div> : (
            <div className="space-y-3">
              {approvedUsers.map((user) => (
                <div key={user.id} className="bg-white bg-opacity-10 rounded-lg p-4 flex justify-between items-center">
                  <div>
                    <h3 className="font-bold text-gray-800">{user.name || 'Unknown'}</h3>
                    <p className="text-sm opacity-90 text-gray-700">{user.email}</p>
                    {/* 🟢 [修改] Pending 列表适配新字段 */}
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <span className={`text-xs px-2 py-1 rounded-full font-bold text-white ${user.role === 'teacher' ? 'bg-blue-500' : 'bg-green-500'}`}>{user.role}</span>
                      
                      {/* 直接显示后端 populate 好的 schoolName */}
                      {user.schoolName && (
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded border border-gray-200">
                          <AppIcon name="school" className="mr-1" /> {user.schoolName}
                        </span>
                      )}
                      
                      {/* 直接显示后端 populate 好的 classNames */}
                      {user.classNames && user.classNames.length > 0 && (
                        <div className="flex gap-1">
                          {user.classNames.map((clsName, idx) => (
                            <span key={idx} className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded border border-blue-100">
                              <AppIcon name="users" className="mr-1" /> {clsName}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex flex-col items-end gap-2">
                    <button onClick={() => handleEditUserClick(user)} className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm shadow-sm flex items-center w-full justify-center"><AppIcon name="edit" className="mr-1" /> Edit</button>
                    <button onClick={() => openConfirm('delete_user', user.id, 'Remove User', `Delete ${user.name}?`, true)} className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm shadow-sm flex items-center w-full justify-center"><AppIcon name="trash-alt" className="mr-1" /> Remove</button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} />
        </div>
      </div>
    );
  };

  const renderVocabulary = () => {
      // 1. 解构 loading 状态
      const { data: vocabulary, loading, currentPage, totalPages, goToPage, totalItems } = vocabPager;
      
      return (
        <div className="space-y-6">
          <div className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6"><AppIcon name="book" className="mr-2" /> Vocabulary Management</h2>
            
            {/* ... 统计卡片部分 (保持不变) ... */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
               <div className="bg-white bg-opacity-20 rounded-lg p-4 text-center text-gray-800 shadow-sm"><div className="text-3xl font-bold">{vocabStats.total}</div><div className="text-sm opacity-90">Total Words</div></div>
               <div className="bg-white bg-opacity-20 rounded-lg p-4 text-center text-gray-800 shadow-sm"><div className="text-3xl font-bold text-green-600">{vocabStats.approved}</div><div className="text-sm opacity-90">Approved</div></div>
               <div className="bg-white bg-opacity-20 rounded-lg p-4 text-center text-gray-800 shadow-sm"><div className="text-3xl font-bold text-yellow-600">{vocabStats.pending}</div><div className="text-sm opacity-90">Pending</div></div>
               <div className="bg-white bg-opacity-20 rounded-lg p-4 text-center text-gray-800 shadow-sm"><div className="text-3xl font-bold text-red-600">{vocabStats.rejected}</div><div className="text-sm opacity-90">Rejected</div></div>
            </div>

            <div className="text-center">
              <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => { handleCloseModal(); setIsWordModalOpen(true); }} className="bg-white text-purple-600 hover:bg-gray-100 font-bold py-3 px-6 rounded-lg shadow-md"><AppIcon name="plus" className="mr-2" /> Add New Word</motion.button>
            </div>
          </div>

          <div className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-6">
             {/* Filters 区域 */}
             <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                <div className="flex items-center gap-3">
                    <h3 className="text-xl font-bold text-gray-800">All Words</h3>
                    {/* 🟢 [新增] Loading 状态提示文字 */}
                    {loading && (
                        <span className="text-sm text-purple-700 font-medium flex items-center bg-purple-100 px-2 py-1 rounded-full animate-pulse">
                            <AppIcon name="circle-notch" className="mr-2" />
                            Updating...
                        </span>
                    )}
                </div>

                <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                    {/* 搜索框 */}
                    <div className="relative">
                        <input 
                            type="text" 
                            placeholder="Search words..." 
                            value={searchFilters.search || ''} 
                            // 🟢 [修改] 绑定 onChange (注意：此处不要用 null，input value 需要是 string)
                            onChange={(e) => setSearchFilters(prev => ({ ...prev, search: e.target.value }))} 
                            // 🟢 [新增] 加载时禁用
                            disabled={loading}
                            className={`w-full sm:w-48 pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white/80 transition-all ${
                                loading ? 'opacity-60 cursor-not-allowed bg-gray-100 text-gray-500' : ''
                            }`} 
                        />
                        {/* 🟢 [新增] 图标动态切换：加载时显示转圈，平时显示搜索图标 */}
                        <AppIcon name={loading ? 'spinner' : 'search'} className={`${loading ? 'text-purple-600' : 'text-gray-400'} absolute left-3 top-3 text-sm`} />
                    </div>

                    {/* Level 下拉框 */}
                    <select 
                        value={searchFilters.level || 'all'} 
                        onChange={(e) => setSearchFilters(prev => ({ ...prev, level: e.target.value }))} 
                        disabled={loading} // 🟢 [新增] 加载时禁用
                        className={`px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white/80 cursor-pointer transition-all ${
                            loading ? 'opacity-60 cursor-not-allowed bg-gray-100 text-gray-500' : ''
                        }`}
                    >
                        <option value="all">All Levels</option>
                        <option value="core">Core</option>
                        <option value="stretch">Stretch</option>
                        <option value="genius">Genius</option>
                    </select>

                    {/* Status 下拉框 */}
                    <select 
                        value={searchFilters.status || 'all'} 
                        onChange={(e) => setSearchFilters(prev => ({ ...prev, status: e.target.value }))} 
                        disabled={loading} // 🟢 [新增] 加载时禁用
                        className={`px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white/80 cursor-pointer transition-all ${
                            loading ? 'opacity-60 cursor-not-allowed bg-gray-100 text-gray-500' : ''
                        }`}
                    >
                        <option value="all">All Status</option>
                        <option value="approved">Approved</option>
                        <option value="pending">Pending</option>
                        <option value="rejected">Rejected</option>
                    </select>
                </div>
             </div>

             {/* 列表内容区域 */}
             {loading ? (
                // 🟢 [优化] 加载时的占位符 (Skeleton UI) 或简单的 Loading
                <div className="text-center p-12 text-gray-500 flex flex-col items-center">
                    <AppIcon name="spinner" className="text-4xl text-purple-400 mb-4" />
                    <p>Fetching data...</p>
                </div>
             ) : (
               <div className="space-y-4 max-h-[600px] overflow-y-auto custom-scrollbar">
                 {vocabulary.length > 0 ? vocabulary.map((word) => (
                   <div key={word.id} className="bg-white bg-opacity-20 rounded-lg p-4 flex items-center justify-between hover:bg-white/40 transition-colors">
                     <div className="flex items-center gap-4">
                        <img 
                            src={`${getImgUrl(word.imageUrl)}?t=${Date.now()}` || '/images/vocabulary-illustration.jpg'} 
                            onClick={() => setPreviewImage(`${getImgUrl(word.imageUrl)}?t=${Date.now()}` || '/images/vocabulary-illustration.jpg')} 
                            alt={word.word} 
                            className="w-12 h-12 object-cover rounded-md bg-gray-100 cursor-pointer hover:scale-110 transition-transform border border-gray-200" 
                        />
                        <div>
                           <h4 className="font-bold text-gray-800 text-lg capitalize">{word.word} <span className="text-xs font-normal text-gray-500 ml-2">({word.partOfSpeech})</span></h4>
                           <p className="text-sm text-gray-600 mb-1 line-clamp-1 max-w-md">{word.definition}</p>
                           <div className="flex space-x-2">
                               <span className="text-xs bg-purple-500 text-white px-2 py-1 rounded">{word.level}</span>
                               <span className={`text-xs px-2 py-1 rounded ${word.status === 'approved' ? 'bg-green-100 text-green-800' : word.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>{word.status}</span>
                           </div>
                        </div>
                     </div>
                     <div className="flex space-x-2">
                       {word.status === 'pending' && (
                           <>
                               <button onClick={() => openConfirm('update_word_status', word.id, 'Approve Word', `Approve?`, false, 'approved')} className="bg-green-500 text-white px-3 py-1 rounded text-sm hover:bg-green-600">Approve</button>
                               <button onClick={() => openConfirm('update_word_status', word.id, 'Reject Word', `Reject?`, true, 'rejected')} className="bg-yellow-500 text-white px-3 py-1 rounded text-sm hover:bg-yellow-600">Reject</button>
                           </>
                       )}
                       <button onClick={() => handleEditClick(word)} className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm shadow-sm"><AppIcon name="edit" /></button>
                       <button onClick={() => openConfirm('delete_word', word.id, 'Delete Word', `Delete "${word.word}"?`, true)} className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm shadow-sm"><AppIcon name="trash-alt" /></button>
                     </div>
                   </div>
                 )) : <div className="text-center p-8 text-gray-500">No words found.</div>}
               </div>
             )}
             
             {/* 分页组件 (加载时也可以选择隐藏或禁用，这里保持显示但无法点击由 Pagination 内部控制或保持现状) */}
             <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} />
          </div>
        </div>
      );
  };

  // 🟢 [新增] 3. School Management Module
  const renderSchoolManagement = () => {
    const { data: schools, loading, currentPage, totalPages, goToPage, totalItems } = schoolsPager;

    return (
      <div className="space-y-6">
        <div className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-6"><AppIcon name="school" className="mr-2" /> School Management</h2>
          <div className="text-center">
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => { handleCloseSchoolModal(); setIsSchoolModalOpen(true); }} className="bg-white text-blue-600 hover:bg-gray-100 font-bold py-3 px-6 rounded-lg shadow-md">
              <AppIcon name="plus" className="mr-2" /> Add New School
            </motion.button>
          </div>
        </div>

        <div className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-6">
          <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
            <h3 className="text-xl font-bold text-gray-800">All Schools</h3>
            <div className="relative">
                <input type="text" placeholder="Search schools..." value={schoolSearch} onChange={(e) => setSchoolSearch(e.target.value)} className="w-full sm:w-64 pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white/80" />
                <AppIcon name="search" className="absolute left-3 top-3 text-gray-400 text-sm" />
            </div>
          </div>

          {loading ? <div className="text-center p-8">Loading schools...</div> : (
            <div className="space-y-4">
              {schools.length > 0 ? schools.map((school) => (
                <div key={school.id} className="bg-white bg-opacity-20 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                     <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-500 text-xl"><AppIcon name="graduation-cap" /></div>
                     <div>
                        <h4 className="font-bold text-gray-800 text-lg">{school.name}</h4>
                        <p className="text-sm text-gray-600 mb-1">{school.region} • {school.type}</p>
                        <p className="text-xs text-gray-500">{school.contactEmail || 'No contact email'}</p>
                     </div>
                  </div>
                  <div className="flex space-x-2">
                    <button onClick={() => openClassMgmt(school)} className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm shadow-sm" title="Manage Classes"><AppIcon name="users" /></button>
                    <button onClick={() => handleEditSchoolClick(school)} className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm shadow-sm" title="Edit School"><AppIcon name="edit" /></button>
                    {/*<button onClick={() => openConfirm('delete_school', school.id, 'Delete School', `Delete "${school.name}" and all associated data?`, true)} className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm shadow-sm"><AppIcon name="trash-alt" /></button>*/}
                  </div>
                </div>
              )) : <div className="text-center p-8 text-gray-500">No schools found.</div>}
            </div>
          )}
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} />
        </div>
      </div>
    );
  };

  const renderAnalytics = () => { /* ... keeping exact existing logic ... */ 
      if (dashboardLoading) return <div className="p-12 text-center text-gray-500"><AppIcon name="spinner" className="mr-2" />Loading analytics...</div>;
      if (!dashboardData) return null;
      const { cards, chart, growth } = dashboardData;
      const yMax = Math.max(cards.totalWordsLearned, cards.totalUsers) * 1.1 || 100;
      const getX = (i) => (i / (chart.length - 1)) * 100;
      const getY = (val) => 100 - ((val / yMax) * 100);
      const buildPath = (key) => {
        if (!chart.length) return "";
        let d = `M 0,${getY(chart[0][key])} `;
        chart.forEach((item, i) => { if(i>0) d += `L ${getX(i)},${getY(item[key])} `; });
        return d;
      };
      return (
        <div className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white/40 backdrop-blur-md rounded-2xl p-6 text-center shadow-sm"><div className="text-4xl font-bold text-gray-800">{cards.totalUsers}</div><div className="text-sm text-gray-600 font-medium">Total Users</div></div>
            <div className="bg-white/40 backdrop-blur-md rounded-2xl p-6 text-center shadow-sm"><div className="text-4xl font-bold text-gray-800">{cards.totalStudents}</div><div className="text-sm text-gray-600 font-medium">Active Students</div></div>
            <div className="bg-white/40 backdrop-blur-md rounded-2xl p-6 text-center shadow-sm"><div className="text-4xl font-bold text-gray-800">{cards.totalTeachers}</div><div className="text-sm text-gray-600 font-medium">Teachers</div></div>
            <div className="bg-white/40 backdrop-blur-md rounded-2xl p-6 text-center shadow-sm"><div className="text-4xl font-bold text-gray-800">{cards.totalWordsLearned}</div><div className="text-sm text-gray-600 font-medium">Words Learned</div></div>
          </div>
          <div className="bg-white/40 backdrop-blur-md rounded-2xl p-8 shadow-sm">
            <div className="flex justify-between items-center mb-8"><h2 className="text-xl font-bold text-gray-800"><AppIcon name="chart-line" className="mr-2" /> Growth Trends (Last 6 Months)</h2>
              <div className="flex gap-4 text-xs font-semibold">
                <div className="flex items-center"><span className="w-2 h-2 rounded-full bg-blue-500 mr-2"></span>Total Users</div>
                <div className="flex items-center"><span className="w-2 h-2 rounded-full bg-green-500 mr-2"></span>Students</div>
                <div className="flex items-center"><span className="w-2 h-2 rounded-full bg-purple-500 mr-2"></span>Teachers</div>
                <div className="flex items-center"><span className="w-2 h-2 rounded-full bg-orange-500 mr-2"></span>Words Learned</div>
              </div>
            </div>
            <div className="relative h-80 w-full">
              <div className="absolute inset-0 flex flex-col justify-between text-xs text-gray-400">{[1, 0.75, 0.5, 0.25, 0].map(p => (<div key={p} className="border-b border-gray-300/30 w-full h-0 relative"><span className="absolute -top-3 left-0">{Math.floor(yMax * p)}</span></div>))}</div>
              <svg className="absolute inset-0 w-full h-full overflow-visible ml-8" style={{width: 'calc(100% - 2rem)'}} preserveAspectRatio="none">
                <path d={buildPath('users')} fill="none" stroke="#3b82f6" strokeWidth="3" strokeLinecap="round" vectorEffect="non-scaling-stroke"/>
                <path d={buildPath('students')} fill="none" stroke="#10b981" strokeWidth="3" strokeLinecap="round" vectorEffect="non-scaling-stroke"/>
                <path d={buildPath('teachers')} fill="none" stroke="#8b5cf6" strokeWidth="3" strokeLinecap="round" vectorEffect="non-scaling-stroke"/>
                <path d={buildPath('wordsLearned')} fill="none" stroke="#f97316" strokeWidth="3" strokeLinecap="round" vectorEffect="non-scaling-stroke"/>
                {chart.map((d, i) => (<g key={i}>{[ {k: 'users', c: '#3b82f6'}, {k: 'students', c: '#10b981'}, {k: 'teachers', c: '#8b5cf6'}, {k: 'wordsLearned', c: '#f97316'}].map(meta => (<circle key={meta.k} cx={`${getX(i)}%`} cy={`${getY(d[meta.k])}%`} r="4" fill="white" stroke={meta.c} strokeWidth="2"/>))}</g>))}
              </svg>
              <div className="absolute bottom-0 left-8 w-[calc(100%-2rem)] flex justify-between text-xs text-gray-500 translate-y-6">{chart.map((d, i) => (<span key={i}>{d.label}</span>))}</div>
            </div>
            <div className="grid grid-cols-4 gap-4 mt-12 pt-8 border-t border-gray-200/50">
              <div className="text-center"><div className="text-xl font-bold text-blue-500">+{growth.users}%</div><div className="text-xs text-gray-500 mt-1">User Growth</div></div>
              <div className="text-center"><div className="text-xl font-bold text-green-500">+{growth.students}%</div><div className="text-xs text-gray-500 mt-1">Student Growth</div></div>
              <div className="text-center"><div className="text-xl font-bold text-purple-500">+{growth.teachers}%</div><div className="text-xs text-gray-500 mt-1">Teacher Growth</div></div>
              <div className="text-center"><div className="text-xl font-bold text-orange-500">+{growth.wordsLearned}%</div><div className="text-xs text-gray-500 mt-1">Learning Growth</div></div>
            </div>
          </div>
        </div>
      );
  };

  // 🟢 3. 最终版 Render Settings (UI 风格统一化)
  const renderSettings = () => {
    if (loadingSettings) {
      return (
        <div className="p-12 text-center text-gray-500">
          <AppIcon name="spinner" className="mr-2" />Loading settings...
        </div>
      );
    }

    const settingItems = [
      { 
        key: 'autoApprove', 
        label: 'Auto-approve Users', 
        desc: 'Skip manual approval for new student and teacher signups.', 
        isDanger: false 
      },
      {
        // 🟢 [新增] 访客试玩结束后的极速注册要靠这个开关才能立刻拿到积分
        key: 'autoApproveStudents',
        label: 'Auto-approve Students Only',
        desc: 'Approve student signups instantly. Teachers still require manual approval.',
        isDanger: false
      },
      { 
        key: 'emailNotifs', 
        label: 'Email Notifications', 
        desc: 'Get alerts for new pending approvals.', 
        isDanger: false 
      }
    ];

    return (
      <div className="space-y-6">
        {/* ================= General Settings ================= */}
        <div className="bg-sky-100/50 backdrop-blur-md rounded-2xl p-8 border border-sky-200">
          <h2 className="text-xl font-bold text-gray-800 mb-8 flex items-center">
            <AppIcon name="cog" className="mr-2" /> General Settings
          </h2>
          <div className="space-y-0 divide-y divide-sky-200/60">
            {settingItems.map((item) => (
              <div key={item.key} className="flex justify-between items-center py-6 first:pt-0 last:pb-0">
                <div className="pr-8">
                  <h3 className={`font-bold text-base ${item.isDanger ? 'text-red-600' : 'text-gray-800'}`}>
                    {item.label}
                  </h3>
                  {/* 🟢 修改点：text-gray-500 -> text-gray-600 (加深) */}
                  <p className="text-sm text-gray-600 mt-1">{item.desc}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer" 
                    checked={settings[item.key] || false} 
                    onChange={() => handleToggle(item.key)} 
                  />
                  <div className="w-14 h-8 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-500"></div>
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* ================= Curriculum Content ================= */}
        <div className="bg-sky-100/50 backdrop-blur-md rounded-2xl p-8 border border-sky-200">
          <h2 className="text-xl font-bold text-gray-800 mb-8 flex items-center">
            <span className="mr-2">📚</span> Curriculum Content
          </h2>          
          <div className="space-y-0 divide-y divide-sky-200/60">
            <div className="flex justify-between items-center py-6 first:pt-0 last:pb-0">
                <div className="pr-8">
                    {/* 🟢 修改点：flex布局，把问号放在标题后面 */}
                    <h3 className="font-bold text-base text-gray-800 flex items-center gap-2">
                        Generate / Repair Curriculum
                        <button
                          onClick={() => setIsHelpModalOpen(true)}
                          className="text-blue-500 hover:text-blue-700 transition-colors"
                          title="How it works?"
                        >
                          <AppIcon name="question-circle" className="text-lg" />
                        </button>
                    </h3>
                    {/* 🟢 修改点：text-gray-500 -> text-gray-600 (加深) */}
                    <p className="text-sm text-gray-600 mt-1 max-w-xl">
                        Automatically organizes approved vocabulary into lessons. <br/>
                        {/* 🟢 修改点：移除 opacity-75，改用 text-gray-500 (清晰的小字) */}
                        <span className="text-xs text-gray-500 mt-1 inline-block">
                          Fixes lessons with deleted words & creates new lessons for added words.
                        </span>
                    </p>
                </div>
                
                <button
                    onClick={handleGenerateLessons}
                    className="px-6 py-3 rounded-xl font-bold text-white shadow-md transition-all flex items-center bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 hover:shadow-lg active:scale-95 whitespace-nowrap"
                >
                    <AppIcon name="magic" className="mr-2" /> One-Click Generate
                </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 🟢 [新增] 渲染添加/编辑学校表单
  const renderAddSchoolForm = () => (
    <form className="space-y-6" onSubmit={handleAddSchoolSubmit}>
      <div className="grid grid-cols-1 gap-6">
        <div>
          <label className="block text-gray-800 font-bold mb-2">School Name *</label>
          <input type="text" value={newSchool.name} onChange={e => setNewSchool({...newSchool, name: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" required />
        </div>
        <div className="grid grid-cols-2 gap-4">
            <div>
                <label className="block text-gray-800 font-bold mb-2">Region</label>
                <select value={newSchool.region} onChange={e => setNewSchool({...newSchool, region: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="UK">United Kingdom</option><option value="International">International</option>
                </select>
            </div>
            <div>
                <label className="block text-gray-800 font-bold mb-2">Type</label>
                <select value={newSchool.type} onChange={e => setNewSchool({...newSchool, type: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="Primary">Primary School</option><option value="Secondary">Secondary School</option><option value="Language Centre">Language Centre</option>
                </select>
            </div>
        </div>
        <div>
          <label className="block text-gray-800 font-bold mb-2">Contact Email</label>
          <input type="email" value={newSchool.contactEmail} onChange={e => setNewSchool({...newSchool, contactEmail: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
      </div>
      <div className="flex justify-end pt-4 border-t mt-4">
        <button type="button" onClick={handleCloseSchoolModal} className="mr-4 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-bold" disabled={isSubmittingForm}>Cancel</button>
        <button type="submit" disabled={isSubmittingForm} className="px-8 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-bold shadow-lg flex items-center">{isSubmittingForm ? 'Saving...' : 'Save School'}</button>
      </div>
    </form>
  );

  // 🟢 [新增] 渲染编辑用户表单
  const renderEditUserForm = () => (
    <form className="space-y-6" onSubmit={handleEditUserSubmit}>
      <div className="grid grid-cols-1 gap-6">
        <div>
          <label className="block text-gray-800 font-bold mb-2">Role</label>
          <select value={editingUserForm.role} onChange={e => setEditingUserForm({...editingUserForm, role: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="student">Student</option>
            <option value="teacher">Teacher</option>
            {/* 🟢 admin 选项隐藏：不允许通过此弹窗把用户改成管理员 */}
            {editingUserForm.role === 'admin' && <option value="admin">Admin</option>}
          </select>
        </div>
        <div>
          <label className="block text-gray-800 font-bold mb-2">School</label>
          <select value={editingUserForm.schoolId} onChange={handleSchoolChange} className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">-- No School --</option>
            {allSchools.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        {editingUserForm.schoolId && (
          <div>
            <label className="block text-gray-800 font-bold mb-2">Classes</label>
            <div className="max-h-40 overflow-y-auto border rounded-lg p-2 bg-gray-50">
              {schoolClasses.length > 0 ? schoolClasses.map(c => (
                <label key={c.id} className="flex items-center space-x-2 p-2 hover:bg-gray-200 cursor-pointer rounded">
                  <input type="checkbox" checked={editingUserForm.classIds.includes(c.id)} onChange={(e) => {
                    const checked = e.target.checked;
                    setEditingUserForm(prev => ({
                      ...prev,
                      classIds: checked ? [...prev.classIds, c.id] : prev.classIds.filter(id => id !== c.id)
                    }));
                  }} />
                  <span>{c.name}</span>
                </label>
              )) : <div className="text-gray-500 p-2 text-sm">No classes available</div>}
            </div>
          </div>
        )}
      </div>
      <div className="flex justify-end pt-4 border-t mt-4">
        <button type="button" onClick={handleCloseUserModal} className="mr-4 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-bold" disabled={isSubmittingForm}>Cancel</button>
        <button type="submit" disabled={isSubmittingForm} className="px-8 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-bold shadow-lg flex items-center">{isSubmittingForm ? 'Saving...' : 'Save User'}</button>
      </div>
    </form>
  );

  const renderAddWordForm = () => {
    // 逻辑：优先显示本地预览，其次显示服务器图片
    // 技巧：给服务器图片加个随机数或时间戳，防止同名文件的浏览器缓存问题
    const serverImageSrc = newWord.imageUrl ? getImgUrl(newWord.imageUrl) : null;
    
    // 如果刚刚上传成功，serverImageSrc 可能被浏览器缓存了旧图（假如文件名没变）
    // 我们可以加一个 key 或 query param 来强制刷新显示
    // 这里简单处理：getImgUrl 返回的是路径，我们在 img src 处处理
    
    const displayImageSrc = localPreviewUrl || serverImageSrc;

    return (
      <form className="space-y-6" onSubmit={handleAddWordSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          
          {/* 1. Word Input (绑定了严格的 onChange) */}
          <div>
              <label className="block text-gray-800 font-bold mb-2">Word *</label>
              <input 
                  type="text" 
                  value={newWord.word} 
                  onChange={handleWordChange} 
                  // 🟢 [新增] 绑定 onBlur 事件
                  onBlur={handleWordBlur}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                  // 🟢 [修改] 如果有警告，边框变红
                      duplicateWarning 
                        ? 'border-red-500 focus:ring-red-500 bg-red-50' 
                        : 'border-gray-300 focus:ring-purple-500'
                    }`}
                  required 
                  placeholder="Enter word..."
                  // 如果正在上传或已经有待上传文件，建议锁定单词输入，或者像上面 handleWordChange 那样处理
              />
              {/* 提示信息 */}
              {/* 🟢 [新增] 醒目的红色警告文字 */}
              {duplicateWarning && (
                <motion.div 
                  initial={{ opacity: 0, y: -5 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  className="text-red-600 font-bold text-sm mt-2 flex items-center animate-pulse"
                >
                  <AppIcon name="exclamation-circle" className="mr-2" />
                  {duplicateWarning}
                </motion.div>
              )}

              {!duplicateWarning && newWord.word && newWord.imageUrl === '' && !selectedImageFile && (
                  <p className="text-xs text-orange-500 mt-1">Word changed? Please upload image again.</p>
              )}
          </div>

          {/* ... Level, PartOfSpeech 等字段保持不变 ... */}
           <div><label className="block text-gray-800 font-bold mb-2">Level *</label><select value={newWord.level} onChange={e => setNewWord({...newWord, level: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"><option value="core">Core</option><option value="stretch">Stretch</option><option value="genius">Genius</option></select></div>
           <div><label className="block text-gray-800 font-bold mb-2">Part of Speech *</label><select value={newWord.partOfSpeech} onChange={e => setNewWord({...newWord, partOfSpeech: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"><option value="noun">Noun</option><option value="verb">Verb</option><option value="adjective">Adjective</option><option value="adverb">Adverb</option><option value="preposition">Preposition</option><option value="conjunction">Conjunction</option><option value="pronoun">Pronoun</option><option value="interjection">Interjection</option></select></div>
           <div><label className="block text-gray-800 font-bold mb-2">Definition *</label><input type="text" value={newWord.definition} onChange={e => setNewWord({...newWord, definition: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" required /></div>
           <div className="md:col-span-2"><label className="block text-gray-800 font-bold mb-2">Synonyms</label><div className="grid grid-cols-3 gap-4">{newWord.synonyms.map((syn, idx) => (<input key={idx} type="text" placeholder={`Synonym ${idx + 1}`} value={syn} onChange={e => handleSynonymChange(idx, e.target.value)} className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm" />))}</div></div>

          {/* 🟢 [重构] 严格的图片上传区域 */}
          <div className="md:col-span-2">
            <label className="block text-gray-800 font-bold mb-2">Image</label>
            
            <div className={`flex items-start space-x-4 border rounded-lg p-4 bg-gray-50/50 ${!newWord.word ? 'opacity-50 pointer-events-none' : ''}`}>
              
              <div className="flex-1 space-y-3">
                  {/* File Input: 使用 key 强制重置 */}
                  <input 
                      key={fileInputKey} 
                      type="file" 
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100 cursor-pointer"
                  />
                  
                  {/* 只读 URL 框 */}
                  <div className="relative">
                      <input 
                          type="text" 
                          value={newWord.imageUrl} 
                          readOnly 
                          placeholder={newWord.word ? "Upload image to generate URL" : "Enter Word first"}
                          className="w-full px-4 py-2 bg-gray-100 text-gray-500 border border-gray-300 rounded-lg focus:outline-none cursor-not-allowed text-xs" 
                      />
                      {newWord.imageUrl && <AppIcon name="check-circle" className="text-green-500 absolute right-3 top-2.5" />}
                  </div>

                  {/* 仅在有新文件时显示 Upload 按钮 */}
                  {selectedImageFile && (
                      <button 
                          type="button"
                          onClick={handleUploadClick}
                          disabled={isUploadingImage || !newWord.word}
                          className={`w-full py-2 rounded-lg font-bold text-sm shadow-sm transition-all flex items-center justify-center gap-2 ${
                              isUploadingImage 
                              ? 'bg-gray-300 text-gray-600 cursor-wait' 
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                          }`}
                      >
                          {isUploadingImage ? <><AppIcon name="spinner" /> Uploading...</> : <><AppIcon name="cloud-upload-alt" /> Confirm & Upload</>}
                      </button>
                  )}
                  
                  {!newWord.word && <p className="text-xs text-red-500 font-bold">* You must enter the Word before uploading image.</p>}
              </div>

              {/* 预览区 */}
              <div className="w-32 flex-shrink-0 flex flex-col items-center justify-center h-32 bg-gray-200 rounded-lg overflow-hidden border border-gray-300 relative">
                  {displayImageSrc ? (
                      <>
                        <img 
                            // 🔑 技巧：如果是远程图片，添加时间戳参数避免缓存
                            src={localPreviewUrl ? localPreviewUrl : `${displayImageSrc}?t=${Date.now()}`} 
                            alt="Preview" 
                            className={`w-full h-full object-cover ${isUploadingImage ? 'opacity-50 blur-sm' : ''}`}
                        />
                        
                        {/* 状态标签 */}
                        <div className={`absolute bottom-0 inset-x-0 text-white text-[10px] text-center py-0.5 font-bold ${localPreviewUrl ? 'bg-yellow-500' : 'bg-green-500'}`}>
                           {localPreviewUrl ? 'Wait to Upload' : 'Uploaded'}
                        </div>

                        {/* 本地预览模式下允许移除 */}
                        {localPreviewUrl && !isUploadingImage && (
                            <button 
                                type="button"
                                onClick={clearImageState}
                                className="absolute top-1 right-1 bg-black/50 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-black/70"
                            >
                                &times;
                            </button>
                        )}
                      </>
                  ) : (
                      <div className="text-gray-400 flex flex-col items-center">
                          <AppIcon name="image" className="text-2xl mb-1" />
                          <span className="text-[10px]">No Image</span>
                      </div>
                  )}
              </div>
            </div>
          </div>

          <div className="md:col-span-2"><label className="block text-gray-800 font-bold mb-2">Example Sentence *</label><textarea rows="2" value={newWord.example} onChange={e => setNewWord({...newWord, example: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" required /></div>
        </div>
        
        {/* Footer Actions */}
        <div className="flex justify-end pt-4 border-t mt-4">
          
          {/* 🟢 [新增] 提示文字：如果没图，告诉用户为什么按钮不可点 */}
          {!newWord.imageUrl && (
             <span className="text-xs text-red-500 font-bold mr-4 animate-pulse">
               * Image is required to save
             </span>
          )}

          <button type="button" onClick={handleCloseModal} className="mr-4 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-bold" disabled={isSubmittingForm || isUploadingImage}>Cancel</button>
          <button 
            type="submit" 
            // ❌ 严格验证：如果选择了文件但没上传 (selectedImageFile不为空)，禁止保存
            disabled={isSubmittingForm || isUploadingImage || selectedImageFile || !!duplicateWarning || !newWord.imageUrl}
            className={`px-8 py-3 rounded-lg font-bold shadow-lg flex items-center transition-all ${
                  (isSubmittingForm || isUploadingImage || selectedImageFile || !!duplicateWarning) 
                  ? 'bg-gray-400 text-white cursor-not-allowed' // 禁用样式
                  : 'bg-green-500 text-white hover:bg-green-600'
              }`}
            >
            {isSubmittingForm ? 'Saving...' : 'Save Word'}
          </button>
        </div>
      </form>
    );
  };

  const tabs = [
    { id: 'users', name: 'User Management', icon: 'users' },
    { id: 'vocabulary', name: 'Vocabulary', icon: 'book' },
    { id: 'schools', name: 'Schools', icon: 'school' }, // 🟢 [新增] Tab
    { id: 'analytics', name: 'Analytics', icon: 'chart-bar' },
    { id: 'settings', name: 'Settings', icon: 'cog' }
  ];

  // ==========================================
  // 5. Main Render Loop
  // ==========================================

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-100 via-sky-200 to-blue-300 p-6">
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ y: -50 }} animate={{ y: 0 }} className="text-center mb-8">
          <h1 className="text-5xl font-bold text-gray-800 mb-4 font-kid">🛠️ ADMIN PANEL</h1>
        </motion.div>

        {/* Navigation Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 rounded-full font-bold transition-all ${
                activeTab === tab.id ? 'bg-white text-purple-600 ring-4 ring-white/50' : 'bg-white/20 text-gray-800 hover:bg-white/30'
              }`}
            >
              <AppIcon name={tab.icon} className="mr-2" /> {tab.name}
            </button>
          ))}
        </div>

        {/* Main Content Area */}
        <motion.div key={activeTab} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          {activeTab === 'users' && renderUserManagement()}
          {activeTab === 'vocabulary' && renderVocabulary()}
          {activeTab === 'schools' && renderSchoolManagement()} {/* 🟢 [新增] */}
          {activeTab === 'analytics' && renderAnalytics()}
          {activeTab === 'settings' && renderSettings()}
        </motion.div>
      </div>

      {/* 🟢 Modals & Dialogs */}
      <Modal isOpen={isWordModalOpen} onClose={handleCloseModal} title={editingId ? "Edit Word" : "Add New Word"}>{renderAddWordForm()}</Modal>
      <Modal isOpen={isSchoolModalOpen} onClose={handleCloseSchoolModal} title={editingSchoolId ? "Edit School" : "Add New School"}>{renderAddSchoolForm()}</Modal>
      <Modal isOpen={isUserModalOpen} onClose={handleCloseUserModal} title="Edit User">{renderEditUserForm()}</Modal>

      {/* 🟢 [Req3] 班级管理弹窗：在学校下创建/查看班级 */}
      <Modal isOpen={isClassMgmtOpen} onClose={closeClassMgmt} title={classMgmtSchool ? `Classes · ${classMgmtSchool.name}` : 'Classes'}>
        <div className="space-y-5">
          <form onSubmit={handleCreateClass} className="flex gap-2">
            <input type="text" value={newClassName} onChange={e => setNewClassName(e.target.value)} placeholder="New class name (e.g. Year 5A)" className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <button type="submit" disabled={creatingClass || !newClassName.trim()} className="px-5 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-bold shadow-sm disabled:opacity-60 whitespace-nowrap">{creatingClass ? 'Adding...' : 'Add Class'}</button>
          </form>
          <div className="border rounded-lg divide-y max-h-64 overflow-y-auto bg-gray-50">
            {classMgmtLoading ? (
              <div className="p-4 text-center text-gray-500"><AppIcon name="spinner" className="mr-2" />Loading classes...</div>
            ) : classMgmtList.length > 0 ? classMgmtList.map(c => (
              <div key={c.id} className="flex items-center gap-3 p-3">
                <AppIcon name="users" className="text-blue-500" />
                <span className="font-semibold text-gray-800">{c.name}</span>
              </div>
            )) : <div className="p-6 text-center text-gray-400 text-sm">No classes yet. Add one above.</div>}
          </div>
        </div>
      </Modal>

      {/* 🟢 Final Polish: Help Modal with "Best Practice" Warning */}
      <Modal
        isOpen={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
        title="Curriculum Generation Logic"
      >
        <div className="space-y-6 text-gray-700 max-h-[60vh] overflow-y-auto custom-scrollbar p-4">
          
          <p className="text-sm text-gray-600 italic">
            This tool automatically structures your approved vocabulary into a sequential learning path. Here is precisely how the algorithm behaves:
          </p>

          <section className="space-y-4">
            
            {/* 1. Structure Preservation */}
            <div className="flex gap-3">
              <div className="mt-1"><AppIcon name="shield-alt" className="text-blue-500 text-lg" /></div>
              <div>
                <h4 className="font-bold text-gray-800">1. Preserves Existing Structure</h4>
                <p className="text-sm text-gray-600 mt-1">
                  The algorithm treats existing lessons as stable. It will <strong>never</strong> randomly shuffle words or move a word from an earlier lesson to a later one. Lesson 1 will always remain Lesson 1.
                </p>
              </div>
            </div>

            {/* 2. Smart Repair */}
            <div className="flex gap-3">
              <div className="mt-1"><AppIcon name="tools" className="text-green-500 text-lg" /></div>
              <div>
                <h4 className="font-bold text-gray-800">2. Smart Repair & Fill</h4>
                <p className="text-sm text-gray-600 mt-1">
                  If you delete a word from the database, it is automatically removed from its lesson. The system then scans for <em>new, currently unassigned</em> words to fill these specific gaps.
                </p>
              </div>
            </div>

            {/* 3. New Content */}
            <div className="flex gap-3">
              <div className="mt-1"><AppIcon name="plus-circle" className="text-purple-500 text-lg" /></div>
              <div>
                <h4 className="font-bold text-gray-800">3. Incremental Generation</h4>
                <p className="text-sm text-gray-600 mt-1">
                  After repairing existing lessons, any remaining unassigned words are grouped into brand new lessons and appended to the end of the curriculum.
                </p>
              </div>
            </div>

            <hr className="border-gray-200 my-4"/>

            {/* Edge Case Box (Anti-Domino) */}
            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
              <h4 className="font-bold text-amber-800 flex items-center mb-2">
                <AppIcon name="exclamation-triangle" className="mr-2" />
                Edge Case: "The Anti-Domino Rule"
              </h4>
              <p className="text-sm text-amber-900 mb-2">
                What happens if a lesson has a gap (e.g., 11/12 words) but there are <strong>no new words</strong> available to fill it?
              </p>
              <ul className="list-disc list-outside ml-5 text-sm text-amber-900 space-y-1">
                <li>The lesson will temporarily remain at <strong>11 words</strong>.</li>
                <li>It will <strong>NOT</strong> "steal" words from future lessons.</li>
              </ul>
            </div>

            {/* 🟢 [新增] Best Practice Warning - 专门提示不要随便删词 */}
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
              <h4 className="font-bold text-blue-800 flex items-center mb-2">
                <AppIcon name="lightbulb" className="mr-2" />
                Best Practice: Deleting Words
              </h4>
              <p className="text-sm text-blue-900 leading-relaxed">
                Since the system protects lesson order, <strong>arbitrarily deleting words will create gaps</strong> that the system cannot fix without new ingredients.
              </p>
              <p className="text-sm text-blue-900 mt-2 font-semibold">
                Recommendation:
              </p>
              <p className="text-sm text-blue-900">
                Only delete a word if you intend to replace it. Before clicking "Generate", ensure you have added enough <strong>new words</strong> to the database to fill any gaps you created.
              </p>
            </div>

          </section>

          <div className="text-right pt-4">
            <button 
              onClick={() => setIsHelpModalOpen(false)}
              className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-bold transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog isOpen={confirmDialog.isOpen} onClose={closeConfirm} onConfirm={handleConfirmAction} title={confirmDialog.title} message={confirmDialog.message} isLoading={isActionLoading} isDestructive={confirmDialog.isDestructive} />

      {previewImage && (<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-90 p-4 cursor-zoom-out" onClick={() => setPreviewImage(null)}><motion.img initial={{ scale: 0.9 }} animate={{ scale: 1 }} src={previewImage} alt="Full Preview" className="max-w-full max-h-[90vh] rounded-lg shadow-2xl object-contain cursor-default" onClick={(e) => e.stopPropagation()} /><button className="absolute top-6 right-6 text-white text-4xl font-bold hover:text-gray-300 transition-colors" onClick={() => setPreviewImage(null)}>&times;</button></motion.div>)}

      <style>{`.switch { position: relative; display: inline-block; width: 60px; height: 34px; } .switch input { opacity: 0; width: 0; height: 0; } .slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: #ccc; transition: .4s; } .slider:before { position: absolute; content: ""; height: 26px; width: 26px; left: 4px; bottom: 4px; background-color: white; transition: .4s; } input:checked + .slider { background-color: #2196F3; } input:checked + .slider:before { transform: translateX(26px); } .slider.round { border-radius: 34px; } .slider.round:before { border-radius: 50%; }`}</style>
    </div>
  );
};

export default AdminPage;